import { MortgageSimulationDemand } from '@/models/Demand';
import { FFBorrower } from '@/models/ExpertState';
import EnvironmentService from '@/env/EnvironmentService';
import { getBirthDateObjectFromPesel, getGenderFromPesel } from '@/services/utils/PeselUtils';
import { SimulationDTO } from './SimulationDTO';
import { SimulationSummary, SimulationItem, LoanRefusal, DemandParameters, FeeRange } from './subtypes';

export class Simulation<T extends MortgageSimulationDemand = MortgageSimulationDemand> implements SimulationDTO<T> {
  userInput: T;
  summary: SimulationSummary;
  firstPeriodLength?: number;
  offers: SimulationItem[];
  refusedOffers?: SimulationItem[];
  refusedLoans?: string[] | LoanRefusal[];
  demandParameters?: DemandParameters;
  simulationId?: string;


  constructor(dto: SimulationDTO<T>) {
    this.userInput = dto.userInput;
    this.summary = dto.summary;
    this.firstPeriodLength = dto.firstPeriodLength;
    this.offers = dto.offers;
    this.refusedOffers = dto.refusedOffers;
    this.refusedLoans = dto.refusedLoans;
    this.demandParameters = dto.demandParameters;
    this.simulationId = dto.simulationId;
  }

  get LTV(): Nullable<number> {
    return !!this.userInput.loanAmount && !!this.userInput.hypothecValue
      ? Math.round(this.userInput.loanAmount * 10000 / this.userInput.hypothecValue) / 100 : null;
  }

  get availableOferts(): number {
    return this.summary.availableOffersCount!;
  }

  get hypothecValue(): Nullable<number> {
    return this.userInput.hypothecValue || null;
  }

  get creditAmount(): number {
    return this.userInput.loanAmount!;
  }

  get loanPeriod(): number {
    return this.userInput.loanPeriod!;
  }

  get initialCosts(): FeeRange | undefined {
    return this.summary.initialCostRange;
  }

  get initialCostsText(): string {
    return `${this.initialCosts?.from} - ${this.initialCosts?.until} ${EnvironmentService.Environment.currentCurrency()}`;
  }

  get firstInstallment(): FeeRange | undefined {
    return this.summary.firstInstallmentRange;
  }

  get firstInstallmentText(): string {
    return `${this.firstInstallment?.from} - ${this.firstInstallment?.until} ${EnvironmentService.Environment.currentCurrency()}`;
  }

  get lastInstallment(): FeeRange | undefined {
    return this.summary.lastInstallmentRange;
  }

  get lastInstallmentText(): string {
    return `${this.lastInstallment?.from} - ${this.lastInstallment?.until} ${EnvironmentService.Environment.currentCurrency()}`;
  }

  get fixedInstalment(): boolean {
    return this.userInput.fixedInstallments;
  }

  get maxWorthiness() {
    let max = 0;
    this.offers
      .filter(offer => offer.creditWorthiness)
      .forEach(offer => {
        if (offer.creditWorthiness!.maxCreditAmount > max) {
          max = offer.creditWorthiness!.maxCreditAmount;
        }
      });
    return max;
  }

  getClientByPesel(pesel: string): FFBorrower | undefined {
    const {year, month,} = getBirthDateObjectFromPesel(pesel);
    const clientGender = getGenderFromPesel(pesel);

    if (this.userInput.households) {
      const borrowerArray = this.userInput.households.flatMap(households => households.borrowers);

      return borrowerArray.find(borrower => {
        const dateArray = borrower.birthDate.split('-');
        return parseInt(dateArray[0]) === month &&
          parseInt(dateArray[1]) === year &&
          borrower.gender === clientGender;
      });
    }
    return undefined;
  }
}
